import { useCallback, useEffect, useState } from "react";
import { ClipboardText, Warning, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { api, registerError, qty } from "./registerApi";

// The seventh-day discipline: a physical figure per item, entered on day 6
// or 7. Declared (the sum of the daily entries) and actual (opening +
// handovers - count) are shown side by side; a gap beyond 5% is flagged but
// never blocks the close (2 Nov 2026, user: "Do not restrict user for the
// percentage on the closing count for now, allow user to match their
// physical count"). The counted figure becomes next week's opening.
export const ClosingCount = ({ processId, onClosed }) => {
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [reasons, setReasons] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSheet(await api.countSheet(processId));
    } catch (err) {
      setError(registerError(err));
    } finally {
      setLoading(false);
    }
  }, [processId]);

  useEffect(() => { load(); }, [load]);

  const derive = (item) => {
    const raw = counts[item.product_id];
    if (raw === "" || raw == null) return null;
    const counted = Number(raw);
    const actual = Number((item.opening + item.handovers - counted).toFixed(4));
    const variance = Number((item.declared - actual).toFixed(4));
    const pct = Math.abs(actual) < 1e-9
      ? (Math.abs(variance) < 1e-9 ? 0 : 100)
      : Number(((Math.abs(variance) / Math.abs(actual)) * 100).toFixed(2));
    return { counted, actual, variance, pct, beyondTolerance: pct > (sheet?.variance_tolerance_pct ?? 5) };
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = sheet.items.map((i) => ({
        product_id: i.product_id,
        counted_quantity: Number(counts[i.product_id] || 0),
        reason: (reasons[i.product_id] || "").trim() || null,
      }));
      const review = await api.submitCount(processId, payload);
      const trueup = review.sap_trueup || {};
      const sent = (trueup.postings || []).reduce((n, p) => n + p.lines.length, 0);
      toast.success(`Week ${review.week.week_no} closed`, {
        description: trueup.skipped
          ? `Counted figures are now next week's opening. ${trueup.skipped}`
          : sent > 0
            ? `Counted figures are now next week's opening. ${sent} line(s) sent to SAP so the godown matches the count.`
            : "Counted figures are now next week's opening. SAP's godown already matches the count.",
      });
      onClosed?.(review);
    } catch (err) {
      const e = registerError(err);
      toast.error(e.message);
      setError(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-sm text-[#667085]">Loading the count sheet...</div>;
  if (!sheet) {
    return (
      <div className="rounded-sm border border-[#D0D5DD] bg-white p-6 text-center space-y-2" data-testid="count-unavailable">
        <p className="text-sm text-[#667085]">{error?.message || "No open week to count yet."}</p>
      </div>
    );
  }

  const allCounted = sheet.items.every((i) => counts[i.product_id] !== undefined && counts[i.product_id] !== "");

  return (
    <div className="space-y-3" data-testid="closing-count">
      <div className={`rounded-sm border p-3 ${sheet.week.count_due ? "bg-[#FFFAEB] border-[#FEDF89]" : "bg-[#F9FAFB] border-[#D0D5DD]"}`}>
        <h2 className="font-heading text-base font-bold text-[#101828] flex items-center gap-1.5">
          <ClipboardText size={18} weight="bold" /> Closing count · week {sheet.week.week_no}, day {sheet.week.day_index}
        </h2>
        <p className="text-sm text-[#475467] mt-0.5">
          {sheet.week.count_due
            ? "Count what is physically at the line and enter a figure for every item. The count is the truth - it becomes next week's opening balance."
            : `The count opens on day 6. This week runs ${sheet.week.start_date} to ${sheet.week.end_date}.`}
        </p>
      </div>

      {sheet.items.map((item) => {
        const d = derive(item);
        return (
          <div key={item.product_id} className="bg-white border border-[#D0D5DD] rounded-sm p-3 space-y-2" data-testid={`count-item-${item.product_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-sm font-bold text-[#101828] truncate">{item.product_id}</div>
                <div className="text-xs text-[#667085] truncate">{item.description || "—"}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#667085]">book expects</div>
                <div className="text-lg font-bold tabular-nums text-[#1D2939] leading-none">{qty(item.expected_closing)} {item.unit_code}</div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="any"
                inputMode="decimal"
                className="h-14 text-right text-2xl font-bold tabular-nums"
                placeholder="Counted now"
                value={counts[item.product_id] ?? ""}
                onChange={(e) => setCounts((c) => ({ ...c, [item.product_id]: e.target.value }))}
                data-testid={`count-input-${item.product_id}`}
              />
              <span className="text-xs font-bold text-[#475467] w-10 shrink-0">{item.unit_code}</span>
            </div>

            {d && (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-[#F9FAFB] border border-[#EAECF0] rounded-sm p-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#667085]">declared</div>
                  <div className="text-base font-bold tabular-nums text-[#1D2939]">{qty(item.declared)}</div>
                </div>
                <div className="bg-[#F9FAFB] border border-[#EAECF0] rounded-sm p-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#667085]">actual</div>
                  <div className="text-base font-bold tabular-nums text-[#1D2939]">{qty(d.actual)}</div>
                </div>
                <div className={`rounded-sm p-1.5 border ${d.beyondTolerance ? "bg-[#FFFAEB] border-[#FEDF89]" : "bg-[#ECFDF3] border-[#A6F4C5]"}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#667085]">gap</div>
                  <div className={`text-base font-bold tabular-nums ${d.beyondTolerance ? "text-[#B54708]" : "text-[#027A48]"}`}>
                    {qty(d.variance)} · {d.pct}%
                  </div>
                </div>
              </div>
            )}

            {d?.beyondTolerance && (
              <div className="space-y-1.5" data-testid={`reason-block-${item.product_id}`}>
                <p className="text-xs text-[#B54708] flex items-start gap-1.5">
                  <Warning size={14} weight="bold" className="shrink-0 mt-0.5" />
                  More than {sheet.variance_tolerance_pct}% apart - your physical figure still goes in as counted. A
                  note is optional.
                </p>
                <Input
                  list="cr-reason-suggestions"
                  className="h-11"
                  placeholder="optional: spillage / drum short / evaporation / bath dump..."
                  value={reasons[item.product_id] ?? ""}
                  onChange={(e) => setReasons((r) => ({ ...r, [item.product_id]: e.target.value }))}
                  data-testid={`reason-input-${item.product_id}`}
                />
              </div>
            )}
          </div>
        );
      })}

      <datalist id="cr-reason-suggestions">
        {(sheet.reason_suggestions || []).map((r) => <option key={r} value={r} />)}
      </datalist>

      <Button
        className="w-full h-12 text-base"
        disabled={submitting || !allCounted || !sheet.week.count_due}
        onClick={submit}
        data-testid="submit-closing-count"
      >
        <CheckCircle size={18} weight="bold" />
        {submitting ? "Closing the week..." : allCounted ? "Submit count and close the week" : "A figure is needed for every item"}
      </Button>
    </div>
  );
};
